import { useMemo } from 'react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Archive, Plus, RefreshCw } from 'lucide-react';
import {
  useWaapLogArchiveConfigs,
  useWaapLogArchiveBuckets,
  type WaapLogArchiveConfig,
} from '@/api/waap/log-archive';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import styles from './LogsTab.module.css';

/** Read the first present key from a passthrough vendor row (field names not yet
 *  confirmed — the live account has zero configs), formatting arrays/booleans. */
function pick(row: WaapLogArchiveConfig, keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (v == null || v === '') continue;
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'boolean') return v ? 'Enabled' : 'Disabled';
    return String(v);
  }
  return '—';
}

/**
 * WAAP Logs — Log Archiving Configurations.
 *
 * The vendor's WAAP "Logs" page is NOT an on-demand log-file download list (that
 * public Open-API endpoint doesn't exist for WAAP — it 401s). It manages where the
 * account's WAAP logs are archived: OSS bucket + path prefix + scope. Served on the
 * private portal (waap2). We anchor the customer on any one website UUID and list
 * the account's archiving configs.
 *
 * Create is intentionally not wired yet: a config requires an OSS bucket, and the
 * vendor's bucket API is currently broken (the vendor console's own Save is blocked
 * too). Once buckets work we capture the write-contract and enable Add.
 */
export function LogsTab() {
  const { websites, isLoading: wsLoading } = useWaapWebsite();
  const anchor = websites[0]?.domainId;

  const { data: configs, isLoading, isError, error, isFetching, refetch } =
    useWaapLogArchiveConfigs(anchor);
  // Prefetch bucket options so the (future) Add form opens instantly; also lets us
  // reflect the vendor-side bucket outage in the disabled-Add hint.
  const { data: buckets } = useWaapLogArchiveBuckets(anchor);
  const rows = configs ?? [];
  const hasBuckets = (buckets?.length ?? 0) > 0;

  const columns = useMemo<ColumnDef<WaapLogArchiveConfig>[]>(
    () => [
      { id: 'scope', header: 'Archiving Scope', cell: ({ row }) => pick(row.original, ['archiving_scope', 'scope', 'archive_scope']) },
      { id: 'domains', header: 'Archived Domains', cell: ({ row }) => pick(row.original, ['archived_domains', 'domains', 'archive_domains']) },
      { id: 'storage', header: 'Storage Type', cell: ({ row }) => pick(row.original, ['storage_type', 'storage']) },
      { id: 'log_type', header: 'Log Type', cell: ({ row }) => pick(row.original, ['log_type', 'logType', 'type']) },
      { id: 'bucket', header: 'Bucket', cell: ({ row }) => pick(row.original, ['bucket', 'bucket_name']) },
      { id: 'prefix', header: 'Path Prefix', cell: ({ row }) => pick(row.original, ['path_prefix', 'pathPrefix', 'prefix', 'path']) },
      { id: 'status', header: 'Status', cell: ({ row }) => pick(row.original, ['status', 'state', 'enabled']) },
    ],
    [],
  );

  const errMsg =
    (error as { response?: { data?: { message?: string } }; message?: string } | undefined)
      ?.response?.data?.message ??
    (error as { message?: string } | undefined)?.message ??
    'Could not load log archiving configurations.';

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'Logs' }, { label: 'Log Archiving' }]} />}
        title="Log Archiving Configurations"
        actions={
          <div className={styles.actionsRow}>
            <Button
              startIcon={<Plus size={16} />}
              disabled={!hasBuckets}
              title={hasBuckets ? undefined : 'Create an OSS bucket first — bucket storage is required to archive logs.'}
            >
              Add Log Archiving
            </Button>
            <Button
              variant="secondary"
              startIcon={<RefreshCw size={16} />}
              onClick={() => void refetch()}
              disabled={isFetching || !anchor}
            >
              Refresh
            </Button>
          </div>
        }
      />

      <div className={styles.stack}>
        {!hasBuckets && (
          <Alert tone="info" title="No OSS bucket available">
            Log archiving writes to an OSS bucket. None are available for this account yet,
            so a configuration can't be created. Add a bucket in OSS storage, then return here.
          </Alert>
        )}

        {isError && (
          <Alert tone="danger" title="Failed to load log archiving">
            {errMsg}
          </Alert>
        )}

        {wsLoading || isLoading ? (
          <Skeleton style={{ height: 240 }} />
        ) : (
          <div className={styles.tableScroll}>
            <DataTable
              columns={columns}
              data={rows}
              emptyState={
                <EmptyState
                  size="sm"
                  icon={<Archive size={20} />}
                  title="No log archiving configured"
                  description="No WAAP log-archiving configurations exist for this account yet."
                />
              }
            />
          </div>
        )}
      </div>
    </Container>
  );
}
